
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';

interface Status {
  id: string;
  label: string;
  color: string;
  order_index: number;
}

interface Configuration {
  id: string;
  name: string;
  description?: string;
}

interface CustomField {
  name: string;
  type: string;
}

interface Installateur {
  id: string;
  name: string;
  description?: string;
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('statuses');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // États pour les données Supabase
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [installateurs, setInstallateurs] = useState<Installateur[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>(() => {
    const saved = localStorage.getItem('crm-custom-fields');
    return saved ? JSON.parse(saved) : [
      { name: "Secteur d'activité", type: "text" },
      { name: "Surface", type: "text" }
    ];
  });
  const [newField, setNewField] = useState({ name: '', type: 'text' });

  const [newStatus, setNewStatus] = useState({ label: '', color: '#3B82F6' });
  const [newConfig, setNewConfig] = useState({ name: '', description: '' });
  const [newInstallateur, setNewInstallateur] = useState({ name: '', description: '' });
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [editingConfig, setEditingConfig] = useState<Configuration | null>(null);
  const [editingInstallateur, setEditingInstallateur] = useState<Installateur | null>(null);
  const [draggedStatus, setDraggedStatus] = useState<Status | null>(null);

  // Charger les paramètres depuis Supabase avec filtrage par société
  const loadSettings = async () => {
    try {
      setLoading(true);
      
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (!currentUser.company_id) {
        setError('Société non trouvée');
        return;
      }

      // Charger statuts avec company_id
      const statusesResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=statuses&company_id=${currentUser.company_id}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        }
      });
      const statusResult = await statusesResponse.json();

      // Charger configurations avec company_id
      const configurationsResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=configurations&company_id=${currentUser.company_id}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        }
      });
      const configResult = await configurationsResponse.json();

      // Charger installateurs avec company_id
      const installateursResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=installateurs&company_id=${currentUser.company_id}`, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        }
      });
      const installateurResult = await installateursResponse.json();

      if (statusResult.success) setStatuses(statusResult.statuses || []);
      if (configResult.success) setConfigurations(configResult.configurations || []);
      if (installateurResult.success) setInstallateurs(installateurResult.installateurs || []);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      alert('Erreur de connexion à Supabase');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Sauvegarde des champs personnalisés (local seulement)
  useEffect(() => {
    localStorage.setItem('crm-custom-fields', JSON.stringify(customFields));
  }, [customFields]);

  // Fonctions de drag and drop pour les statuts
  const handleDragStart = (status: Status) => {
    setDraggedStatus(status);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: Status) => {
    e.preventDefault();
    
    if (!draggedStatus || draggedStatus.id === targetStatus.id) {
      setDraggedStatus(null);
      return;
    }

    try {
      setLoading(true);

      // Créer une nouvelle liste avec l'ordre mis à jour
      const updatedStatuses = [...statuses];
      const draggedIndex = updatedStatuses.findIndex(s => s.id === draggedStatus.id);
      const targetIndex = updatedStatuses.findIndex(s => s.id === targetStatus.id);

      // Supprimer l'élément de sa position actuelle et l'insérer à la nouvelle position
      const [removed] = updatedStatuses.splice(draggedIndex, 1);
      updatedStatuses.splice(targetIndex, 0, removed);

      // Mettre à jour les order_index
      const statusesToUpdate = updatedStatuses.map((status, index) => ({
        ...status,
        order_index: index + 1
      }));

      // Mettre à jour tous les statuts dans Supabase
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      for (const status of statusesToUpdate) {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'update-status',
            id: status.id,
            order_index: status.order_index
          })
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la mise à jour de l\'ordre');
        }
      }

      // Recharger les données pour confirmer les changements
      await loadSettings();

    } catch (error) {
      console.error('Erreur lors de la réorganisation:', error);
      alert('Erreur lors de la réorganisation des statuts');
    } finally {
      setLoading(false);
      setDraggedStatus(null);
    }
  };

  // Gestion des statuts
  const createStatus = async (statusData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create-status',
          ...statusData
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
        setNewStatus({ label: '', color: '#3B82F6' });
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const updateStatus = async (statusData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update-status',
          ...statusData
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
        setEditingStatus(null);
        setNewStatus({ label: '', color: '#3B82F6' });
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const deleteStatus = async (statusId: string) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete-status',
          id: statusId
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const editStatus = (status: Status) => {
    setEditingStatus(status);
    setNewStatus({ label: status.label, color: status.color });
  };

  // Gestion des configurations
  const createConfiguration = async (configData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create-configuration',
          ...configData
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
        setNewConfig({ name: '', description: '' });
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const updateConfiguration = async (configData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update-configuration',
          ...configData
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
        setEditingConfig(null);
        setNewConfig({ name: '', description: '' });
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const deleteConfiguration = async (configId: string) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete-configuration',
          id: configId
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const editConfiguration = (config: Configuration) => {
    setEditingConfig(config);
    setNewConfig({ name: config.name, description: config.description || '' });
  };

  // Gestion des installateurs
  const createInstallateur = async (installateurData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create-installateur',
          ...installateurData
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
        setNewInstallateur({ name: '', description: '' });
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const updateInstallateur = async (installateurData: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update-installateur',
          ...installateurData
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
        setEditingInstallateur(null);
        setNewInstallateur({ name: '', description: '' });
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const deleteInstallateur = async (installateurId: string) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?company_id=${currentUser.company_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete-installateur',
          id: installateurId
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSettings();
      } else {
        alert('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur de connexion au serveur');
    }
  };

  const editInstallateur = (installateur: Installateur) => {
    setEditingInstallateur(installateur);
    setNewInstallateur({ name: installateur.name, description: installateur.description || '' });
  };

  // Gestion des champs personnalisés (local)
  const addCustomField = () => {
    if (newField.name.trim()) {
      setCustomFields([...customFields, { ...newField }]);
      setNewField({ name: '', type: 'text' });
    }
  };

  const removeCustomField = (index: number) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce champ personnalisé ? Cette action est irréversible.')) {
      setCustomFields(customFields.filter((_, i) => i !== index));
    }
  };

  const cancelEditing = () => {
    setEditingStatus(null);
    setEditingConfig(null);
    setEditingInstallateur(null);
    setNewStatus({ label: '', color: '#3B82F6' });
    setNewConfig({ name: '', description: '' });
    setNewInstallateur({ name: '', description: '' });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
        <div className="flex items-center space-x-2">
          <p className="text-gray-600">Configuration du système CRM</p>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
            <span className="text-xs text-green-600">Supabase connecté</span>
          </div>
        </div>
      </div>

      {/* Message d'information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <i className="ri-information-line text-blue-600 mr-2"></i>
          <div>
            <p className="text-blue-800 text-sm font-semibold">
              ✅ Paramètres synchronisés avec Supabase !
            </p>
            <p className="text-blue-700 text-xs">
              Toutes les modifications sont partagées entre tous les utilisateurs en temps réel
            </p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'statuses', label: 'Statuts clients', icon: 'ri-flag-line' },
              { id: 'configurations', label: 'Configurations', icon: 'ri-settings-3-line' },
              { id: 'installateurs', label: 'Installateurs', icon: 'ri-user-settings-line' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Indicateur de chargement */}
          {loading && (
            <div className="flex items-center justify-center py-4">
              <i className="ri-loader-4-line text-blue-600 animate-spin mr-2"></i>
              <span className="text-gray-600">Synchronisation...</span>
            </div>
          )}

          {/* Onglet Statuts */}
          {activeTab === 'statuses' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion des statuts clients</h3>
              
              {/* Information sur le drag and drop */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <i className="ri-drag-drop-line text-green-600 mr-2"></i>
                  <div>
                    <p className="text-green-800 text-sm font-semibold">
                      🎯 Réorganisation par glisser-déposer !
                    </p>
                    <p className="text-green-700 text-xs">
                      Glissez et déposez les statuts pour changer leur ordre d'affichage dans les fiches clients
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Ajouter/Modifier un statut */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {editingStatus ? 'Modifier le statut' : 'Ajouter un nouveau statut'}
                </h4>
                <div className="flex space-x-3">
                  <Input
                    placeholder="Nom du statut"
                    value={newStatus.label}
                    onChange={(e) => setNewStatus({ ...newStatus, label: e.target.value })}
                    className="flex-1"
                    disabled={loading}
                  />
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600">Couleur:</label>
                    <input
                      type="color"
                      value={newStatus.color}
                      onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      disabled={loading}
                    />
                  </div>
                  {editingStatus ? (
                    <>
                      <Button
                        onClick={() => updateStatus({ id: editingStatus.id, label: newStatus.label, color: newStatus.color })}
                        disabled={loading}
                      >
                        <i className="ri-save-line mr-2"></i>
                        Modifier
                      </Button>
                      <Button variant="secondary" onClick={cancelEditing} disabled={loading}>
                        Annuler
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => createStatus({ label: newStatus.label, color: newStatus.color, order_index: statuses.length + 1 })}
                      disabled={loading}
                    >
                      <i className="ri-add-line mr-2"></i>
                      Ajouter
                    </Button>
                  )}
                </div>
              </div>

              {/* Liste des statuts avec drag and drop */}
              <div className="space-y-3">
                {statuses.map((status) => (
                  <div 
                    key={status.id} 
                    draggable
                    onDragStart={() => handleDragStart(status)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, status)}
                    className={`flex items-center justify-between p-4 border-2 rounded-lg transition-all duration-200 cursor-move ${
                      draggedStatus?.id === status.id 
                        ? 'border-blue-500 bg-blue-50 shadow-lg' 
                        : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <i className="ri-drag-move-2-line text-gray-400 mr-3 cursor-move"></i>
                      <div 
                        className="w-4 h-4 rounded-full mr-3"
                        style={{ backgroundColor: status.color }}
                      ></div>
                      <span className="font-medium text-gray-900">{status.label}</span>
                      <span className="text-xs text-gray-500 ml-2 bg-gray-100 px-2 py-1 rounded">
                        Position {status.order_index}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => editStatus(status)}
                        className="text-blue-600 hover:text-blue-900 cursor-pointer"
                        title="Modifier"
                        disabled={loading}
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button 
                        onClick={() => deleteStatus(status.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                        title="Supprimer"
                        disabled={loading}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Onglet Configurations */}
          {activeTab === 'configurations' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion des configurations</h3>
              
              {/* Ajouter/Modifier une configuration */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {editingConfig ? 'Modifier la configuration' : 'Ajouter une nouvelle configuration'}
                </h4>
                <div className="space-y-3">
                  <Input
                    placeholder="Nom de la configuration"
                    value={newConfig.name}
                    onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                    disabled={loading}
                  />
                  <Input
                    placeholder="Description"
                    value={newConfig.description}
                    onChange={(e) => setNewConfig({ ...newConfig, description: e.target.value })}
                    disabled={loading}
                  />
                  <div className="flex space-x-3">
                    {editingConfig ? (
                      <>
                        <Button
                          onClick={() => updateConfiguration({ id: editingConfig.id, name: newConfig.name, description: newConfig.description })}
                          disabled={loading}
                        >
                          <i className="ri-save-line mr-2"></i>
                          Modifier
                        </Button>
                        <Button variant="secondary" onClick={cancelEditing} disabled={loading}>
                          Annuler
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => createConfiguration({ name: newConfig.name, description: newConfig.description })}
                        disabled={loading}
                      >
                        <i className="ri-add-line mr-2"></i>
                        Ajouter
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Liste des configurations */}
              <div className="space-y-3">
                {configurations.map((config) => (
                  <div key={config.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div>
                      <p className="font-medium text-gray-900">{config.name}</p>
                      {config.description && (
                        <p className="text-sm text-gray-600">{config.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => editConfiguration(config)}
                        className="text-blue-600 hover:text-blue-900 cursor-pointer"
                        title="Modifier"
                        disabled={loading}
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button 
                        onClick={() => deleteConfiguration(config.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                        title="Supprimer"
                        disabled={loading}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Onglet Installateurs */}
          {activeTab === 'installateurs' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion des installateurs</h3>
              
              {/* Ajouter/Modifier un installateur */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {editingInstallateur ? 'Modifier l\'installateur' : 'Ajouter un nouvel installateur'}
                </h4>
                <div className="space-y-3">
                  <Input
                    placeholder="Nom de l'installateur"
                    value={newInstallateur.name}
                    onChange={(e) => setNewInstallateur({ ...newInstallateur, name: e.target.value })}
                    disabled={loading}
                  />
                  <Input
                    placeholder="Description"
                    value={newInstallateur.description}
                    onChange={(e) => setNewInstallateur({ ...newInstallateur, description: e.target.value })}
                    disabled={loading}
                  />
                  <div className="flex space-x-3">
                    {editingInstallateur ? (
                      <>
                        <Button
                          onClick={() => updateInstallateur({ id: editingInstallateur.id, name: newInstallateur.name, description: newInstallateur.description })}
                          disabled={loading}
                        >
                          <i className="ri-save-line mr-2"></i>
                          Modifier
                        </Button>
                        <Button variant="secondary" onClick={cancelEditing} disabled={loading}>
                          Annuler
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => createInstallateur({ name: newInstallateur.name, description: newInstallateur.description })}
                        disabled={loading}
                      >
                        <i className="ri-add-line mr-2"></i>
                        Ajouter
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Liste des installateurs */}
              <div className="space-y-3">
                {installateurs.map((installateur) => (
                  <div key={installateur.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div>
                      <p className="font-medium text-gray-900">{installateur.name}</p>
                      {installateur.description && (
                        <p className="text-sm text-gray-600">{installateur.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => editInstallateur(installateur)}
                        className="text-blue-600 hover:text-blue-900 cursor-pointer"
                        title="Modifier"
                        disabled={loading}
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button 
                        onClick={() => deleteInstallateur(installateur.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                        title="Supprimer"
                        disabled={loading}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
                
                {installateurs.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <i className="ri-user-settings-line text-4xl mb-2"></i>
                    <p>Aucun installateur configuré</p>
                    <p className="text-sm">Ajoutez des installateurs pour les assigner à vos clients</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
